/**
 * EnterpriseTraining — Round 75 (A-C-05)
 *
 * Floo-aware training VISIBILITY page. Scope-aware activity surface.
 * Independent from effectiveness modeling — counts usage only.
 *
 * Wording discipline: "Most used", "Most engaged", "Last accessed".
 * Never "Most effective" / "Best".
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Loader2,
  GraduationCap,
  ShieldAlert,
  BookOpen,
  Users,
  Activity,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  LineChart,
  Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useFlooActiveScopeContext } from '@/hooks/useFlooActiveScopeContext';
import { ActiveScopeBadge } from '@/components/floo-network/ActiveScopeBadge';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import {
  useScopeTrainingAnalytics,
  type ScopeTrainingFilters,
} from '@/hooks/useScopeTrainingAnalytics';
import {
  useScopeTrainingEffectiveness,
  BEFORE_AFTER_N,
  type ConfidenceLevel,
  type ScopeArtifactIntelligenceRow,
} from '@/hooks/useScopeTrainingEffectiveness';
import type { InScopeScopeType } from '@/hooks/useInScopeTalent';
import { useInScopeTalent } from '@/hooks/useInScopeTalent';
import { formatDistanceToNow } from 'date-fns';
import { WhyButton } from '@/components/intelligence/WhyButton';
import {
  explainTrainingSummary,
  explainTrainingUsage,
  explainTechnicianEngagement,
  explainObservedImpact,
} from '@/lib/intelligence-explanations/builders';

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  short_video: 'Short video',
  long_video: 'Long video',
  live_seminar: 'Live seminar',
  book: 'Book',
  ebook: 'eBook',
};

function fmtType(t: string | null | undefined) {
  if (!t) return '—';
  return ARTIFACT_TYPE_LABELS[t] ?? t;
}

export default function EnterpriseTraining() {
  const navigate = useNavigate();
  const { canViewInScopeTalent, scopeLabel } = useEnterpriseScope();
  // Phase 8 — active scope when chosen; membership fallback otherwise.
  const activeCtx = useFlooActiveScopeContext();
  const scopeType = activeCtx.scopeType;
  const scopeId = activeCtx.scopeId;

  const [shopFilter, setShopFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filters: ScopeTrainingFilters = useMemo(() => {
    const f: ScopeTrainingFilters = {};
    if (shopFilter !== 'all') f.shop_ids = [shopFilter];
    if (typeFilter !== 'all') f.artifact_type = typeFilter;
    if (search.trim()) f.search = search.trim();
    return f;
  }, [shopFilter, typeFilter, search]);

  const { summary, artifacts, technicians, isLoading, error } =
    useScopeTrainingAnalytics({
      scopeType: (scopeType as InScopeScopeType) ?? null,
      scopeId: scopeId ?? null,
      filters,
    });

  const {
    intelligence,
    isLoading: isLoadingImpact,
    error: impactError,
  } = useScopeTrainingEffectiveness({
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
  });

  // Re-use in-scope talent hook for the shop filter list (already cached page-side)
  const { data: talentRows } = useInScopeTalent({
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
  });

  const shops = useMemo(() => {
    const m = new Map<string, string>();
    (talentRows ?? []).forEach((r) => m.set(r.shop_id, r.shop_name));
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [talentRows]);

  const types = useMemo(() => {
    const s = new Set<string>();
    (artifacts ?? []).forEach((a) => s.add(a.artifact_type));
    return Array.from(s).sort();
  }, [artifacts]);

  if (!canViewInScopeTalent) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Access Restricted</CardTitle>
            </div>
            <CardDescription>
              In-scope training visibility is available to managers, owners, and org
              admins only.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const noActivity = !isLoading && !error && (summary?.total_training_events ?? 0) === 0;

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-4">
      <BackToOptiNetworkButton testId="enterprise-training-back-to-opti-network" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold">{scopeLabel} Training</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only training activity across your scope. Shows what's being used and
            by whom — not whether it's effective.
          </p>
          {/* Phase 8 — active scope indicator. */}
          <ActiveScopeBadge className="mt-2" testId="floo-active-scope-badge-training" />
        </div>
        <Link
          to="/enterprise/intelligence/governance"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline shrink-0 mt-1"
        >
          Methodology
        </Link>
      </div>

      <Tabs value="training" className="w-full">
        <TabsList>
          <TabsTrigger value="directory" onClick={() => navigate('/enterprise/talent')}>
            Directory
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            onClick={() => navigate('/enterprise/talent/analytics')}
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
          <TabsTrigger
            value="suggestions"
            onClick={() => navigate('/enterprise/talent/suggestions')}
          >
            Suggestions
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          icon={<Activity className="h-4 w-4 text-primary" />}
          label="Total training events"
          value={isLoading ? '…' : (summary?.total_training_events ?? 0).toLocaleString()}
          why={
            <WhyButton
              entityId="total_events"
              build={() => explainTrainingSummary('total_events', summary ?? null)}
            />
          }
        />
        <SummaryCard
          icon={<Users className="h-4 w-4 text-primary" />}
          label="Technicians engaged"
          value={
            isLoading ? '…' : (summary?.unique_technicians_engaged ?? 0).toLocaleString()
          }
          why={
            <WhyButton
              entityId="engaged_techs"
              build={() => explainTrainingSummary('engaged_techs', summary ?? null)}
            />
          }
        />
        <SummaryCard
          icon={<BookOpen className="h-4 w-4 text-primary" />}
          label="Avg artifacts / technician"
          value={
            isLoading
              ? '…'
              : summary?.avg_artifacts_per_technician != null
                ? Number(summary.avg_artifacts_per_technician).toFixed(2)
                : '—'
          }
          hint="Distinct artifacts per engaged technician"
          why={
            <WhyButton
              entityId="avg_per_tech"
              build={() => explainTrainingSummary('avg_per_tech', summary ?? null)}
            />
          }
        />
        <SummaryCard
          icon={<Sparkles className="h-4 w-4 text-primary" />}
          label="Most active format"
          value={isLoading ? '…' : fmtType(summary?.top_artifact_type)}
          hint="Most-used artifact format in scope"
          why={
            <WhyButton
              entityId="top_format"
              build={() => explainTrainingSummary('top_format', summary ?? null)}
            />
          }
        />
      </div>

      {error && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive text-center">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {noActivity ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base">No training activity yet</CardTitle>
            </div>
            <CardDescription>
              Once technicians in your {scopeLabel.toLowerCase()} start engaging with
              training artifacts, their activity will appear here.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* Filters */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Search artifact title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="sm:max-w-xs"
                />
                <Select value={shopFilter} onValueChange={setShopFilter}>
                  <SelectTrigger className="sm:w-[200px]">
                    <SelectValue placeholder="All shops" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All shops</SelectItem>
                    {shops.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="sm:w-[180px]">
                    <SelectValue placeholder="All formats" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All formats</SelectItem>
                    {types.map((t) => (
                      <SelectItem key={t} value={t}>
                        {fmtType(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
          </Card>

          {/* Top Artifacts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Most used artifacts</CardTitle>
              <CardDescription>
                Ordered by usage count. Activity-only — no effectiveness inference.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : artifacts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No artifacts match your filters.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead className="text-right">Uses</TableHead>
                      <TableHead className="text-right">Technicians</TableHead>
                      <TableHead className="text-right">Last accessed</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {artifacts.map((a) => (
                      <TableRow key={a.artifact_id}>
                        <TableCell className="font-medium">{a.artifact_title}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {fmtType(a.artifact_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {a.provider_name}
                        </TableCell>
                        <TableCell className="text-right">{a.total_uses}</TableCell>
                        <TableCell className="text-right">
                          {a.unique_technicians}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {a.last_used_at
                            ? formatDistanceToNow(new Date(a.last_used_at), {
                                addSuffix: true,
                              })
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <WhyButton
                            entityType="artifact"
                            entityId={a.artifact_id}
                            build={() => explainTrainingUsage(a)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Technician engagement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Most engaged technicians</CardTitle>
              <CardDescription>
                Training activity per technician in your scope.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : technicians.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No technicians match your filters.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Shop</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Unique artifacts</TableHead>
                      <TableHead className="text-right">Last activity</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {technicians.map((t) => (
                      <TableRow key={`${t.user_id}-${t.shop_id}`}>
                        <TableCell>
                          <Link
                            to={`/enterprise/talent/${t.user_id}?mode=in_scope`}
                            className="text-primary font-medium hover:underline"
                          >
                            {t.display_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{t.shop_name}</TableCell>
                        <TableCell className="text-right">
                          {t.training_events_count}
                        </TableCell>
                        <TableCell className="text-right">
                          {t.unique_artifacts_used}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {t.last_training_at
                            ? formatDistanceToNow(new Date(t.last_training_at), {
                                addSuffix: true,
                              })
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <WhyButton
                            entityType="technician_training"
                            entityId={t.user_id}
                            build={() => explainTechnicianEngagement(t)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Observed Impact (R76) */}
          <ObservedImpactSection
            rows={intelligence}
            isLoading={isLoadingImpact}
            error={impactError}
          />
        </>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
  why,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  why?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
            {icon}
            {label}
          </div>
          {why}
        </div>
        <div className="mt-2 text-2xl font-semibold">{value}</div>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Observed Impact (R76 — A-C-06 + A-C-07)
// Strictly observational. No causal language. No composite scores.
// ============================================================================

const CONFIDENCE_STYLES: Record<ConfidenceLevel, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  high: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

const FLAG_LABELS: Record<string, string> = {
  high_usage_low_signal: 'High usage, low signal',
  low_usage_high_signal: 'Low usage, strong signal',
};

/** ↑ improvement / ↓ decline / ~ no significant change. Threshold: 0.02. */
function DeltaCell({
  value,
  invert = false,
}: {
  value: number | null | undefined;
  invert?: boolean;
}) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const v = Number(value);
  const abs = Math.abs(v);
  if (abs < 0.02) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        {v.toFixed(2)}
      </span>
    );
  }
  // For "comeback rate", a decrease is good — invert the up/down semantics.
  const isImprovement = invert ? v < 0 : v > 0;
  const Icon = isImprovement ? TrendingUp : TrendingDown;
  const cls = isImprovement
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-destructive';
  const sign = v > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {sign}
      {v.toFixed(2)}
    </span>
  );
}

function ConfidenceBadge({ level }: { level: ConfidenceLevel }) {
  return (
    <Badge variant="outline" className={`${CONFIDENCE_STYLES[level]} border-0 text-xs capitalize`}>
      {level}
    </Badge>
  );
}

function HeaderWithTip({
  label,
  tip,
  align = 'left',
}: {
  label: string;
  tip: string;
  align?: 'left' | 'right';
}) {
  return (
    <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function ObservedImpactSection({
  rows,
  isLoading,
  error,
}: {
  rows: ScopeArtifactIntelligenceRow[];
  isLoading: boolean;
  error: Error | null;
}) {
  // Only artifacts with at least one qualifying technician carry meaningful signal.
  const measurable = rows.filter((r) => r.sample_size > 0);

  return (
    <TooltipProvider delayDuration={150}>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <LineChart className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Observed Impact</CardTitle>
          </div>
          <CardDescription>
            Observational comparison of the {BEFORE_AFTER_N} jobs each technician completed
            before vs. after engaging with an artifact. Correlation only — not causation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive py-6 text-center">
              {error.message}
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : measurable.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center">
              <p className="text-sm font-medium">No effectiveness data yet</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Needs at least {BEFORE_AFTER_N} completed tickets before AND after a tracked
                training engagement per technician. Signal will accumulate as activity grows.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artifact</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead className="text-right">
                    <HeaderWithTip
                      align="right"
                      label="Sample"
                      tip={`Number of technicians with at least ${BEFORE_AFTER_N} completed tickets both before and after their first tracked engagement with this artifact.`}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <HeaderWithTip
                      align="right"
                      label="Efficiency Δ"
                      tip="Average per-tech change in (predicted ÷ actual) minutes between the before and after windows. Higher = faster relative to estimate. Observational only."
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <HeaderWithTip
                      align="right"
                      label="Comeback Δ"
                      tip="Average per-tech change in comeback rate between the before and after windows. Lower (negative) is better. Observational only."
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <HeaderWithTip
                      align="right"
                      label="Diagnostic Δ"
                      tip="Per-tech change in diagnostic accuracy. Requires at least 3 outcomes on each side, otherwise omitted for that tech. Observational only."
                    />
                  </TableHead>
                  <TableHead>
                    <HeaderWithTip
                      label="Confidence"
                      tip="Based on qualifying technician count: Low <5, Medium 5–15, High >15. Reflects sample size, not predictive power."
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {measurable.map((r) => (
                  <TableRow key={r.artifact_id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col gap-1">
                        <span>{r.artifact_title}</span>
                        {r.flags?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {r.flags.map((f) => (
                              <Badge
                                key={f}
                                variant="outline"
                                className="text-[10px] font-normal"
                              >
                                {FLAG_LABELS[f] ?? f}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {fmtType(r.artifact_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{r.sample_size}</TableCell>
                    <TableCell className="text-right">
                      <DeltaCell value={r.efficiency_delta} />
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaCell value={r.comeback_delta} invert />
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaCell value={r.diagnostic_delta} />
                    </TableCell>
                    <TableCell>
                      <div className="inline-flex items-center gap-1">
                        <ConfidenceBadge level={r.confidence_level} />
                        <WhyButton
                          entityType="artifact"
                          entityId={r.artifact_id}
                          build={() => explainObservedImpact(r)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
